import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Ambulance, Medal, ShieldCheck, Copy, X, Handshake } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { FadeUp } from "./Reveal";

const field = "w-full bg-ink border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory placeholder:text-stone-mist/50 transition-colors";

const SCHEMES = {
  blue_light: {
    icon: Ambulance,
    title: "Blue Light Card",
    referenceLabel: "Blue Light Card number",
    blurb: "For NHS, emergency services, social care and armed forces Blue Light Card holders.",
  },
  veterans: {
    icon: Medal,
    title: "Veterans",
    referenceLabel: "Veterans ID / service reference",
    blurb: "For those who have served in the Armed Forces.",
  },
  marshalls: {
    icon: Handshake,
    title: "Marshall's",
    referenceLabel: "Marshall's card / reference",
    blurb: "For those who marshall for the TT.",
  },
};

function VerifyModal({ scheme, config, onClose }) {
  const meta = SCHEMES[scheme];
  const [form, setForm] = useState({ name: "", email: "", reference: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [code, setCode] = useState(""); // shown instantly only when a provider is connected
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const { data } = await api.post("/discounts/verify-request", { scheme, ...form });
      if (data.code) setCode(data.code); // future: instant provider verification
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const percent = config?.schemes?.[scheme]?.percent ?? 10;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-ink/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 32, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 32, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-ink-2 border border-gold/25 p-8 max-h-[90vh] overflow-y-auto"
        data-testid="verify-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <meta.icon className="w-6 h-6 text-gold" />
            <h3 className="font-serif text-2xl text-ivory font-light">{meta.title} Discount</h3>
          </div>
          <button onClick={onClose} data-testid="verify-close" className="text-stone-mist hover:text-gold">
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          code ? (
            <div className="text-center" data-testid="verify-success">
              <ShieldCheck className="w-10 h-10 text-gold mx-auto mb-4" />
              <p className="font-serif text-2xl text-ivory mb-2">Verification Successful ✓</p>
              <p className="text-stone-mist text-sm mb-6">Your exclusive {percent}% discount code</p>
              <p className="font-mono text-2xl tracking-[0.3em] text-gold border border-gold/40 bg-gold/5 py-4 mb-4" data-testid="discount-code">{code}</p>
              <button
                onClick={() => { navigator.clipboard.writeText(code); toast.success("Code copied"); }}
                data-testid="copy-code"
                className="inline-flex items-center gap-2 border border-gold/40 text-gold px-6 py-3 text-xs uppercase tracking-[0.25em] hover:bg-gold hover:text-ink transition-colors"
              >
                <Copy className="w-4 h-4" /> Copy Code
              </button>
              <p className="text-stone-mist text-xs mt-6">Your code has been generated specifically for you. Enter it at checkout to receive your {percent}% discount.</p>
              <a href="/shop/all" data-testid="verify-shop-now" className="inline-block mt-6 bg-gold text-ink px-8 py-3.5 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors">Shop Now →</a>
            </div>
          ) : (
            <div className="text-center" data-testid="verify-received">
              <ShieldCheck className="w-10 h-10 text-gold mx-auto mb-4" />
              <p className="font-serif text-2xl text-ivory mb-3">Request received</p>
              <p className="text-stone-mist text-sm font-light leading-relaxed">
                Thank you, {form.name.split(" ")[0]}. The Celtic Gold team will verify your {meta.title} eligibility
                and email your personal {percent}% discount code to <span className="text-ivory">{form.email}</span>.
              </p>
              <p className="text-stone-mist/70 text-xs mt-4">Your code will be unique to you, single-use, and time-limited.</p>
            </div>
          )
        ) : (
          <>
            <p className="text-stone-mist text-sm font-light mb-5">
              Please verify your eligibility to access your exclusive discount code.
            </p>
            {!config?.verification_active && (
              <p className="text-xs text-gold/90 border border-gold/25 bg-gold/5 px-4 py-3 mb-5 leading-relaxed" data-testid="verification-mode-note">
                Automated partner verification is being connected. In the meantime, every request is reviewed
                personally by the Celtic Gold team — eligibility is never approved automatically.
              </p>
            )}
            <form onSubmit={submit} className="space-y-4" data-testid="verify-form">
              <input required value={form.name} onChange={set("name")} placeholder="Full name *" className={field} data-testid="verify-name" />
              <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={field} data-testid="verify-email" />
              <input required value={form.reference} onChange={set("reference")} placeholder={`${meta.referenceLabel} *`} className={field} data-testid="verify-reference" />
              <textarea value={form.message} onChange={set("message")} placeholder="Anything we should know? (optional)" rows={2} className={field} data-testid="verify-message" />
              {error && <p className="text-red-400 text-xs" data-testid="verify-error">{error}</p>}
              <button type="submit" disabled={sending} data-testid="verify-submit" className="w-full bg-gold text-ink py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors disabled:opacity-50">
                {sending ? "Submitting..." : `Verify ${meta.title} Eligibility`}
              </button>
              <p className="text-[11px] text-stone-mist/70 leading-relaxed">
                Your details are used only to verify eligibility and are never displayed publicly or shared.
              </p>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function DiscountSection() {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/discounts/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  return (
    <section id="discounts" className="py-20 lg:py-28 border-t border-gold/10" data-testid="discount-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
        <FadeUp>
          <p className="text-xs uppercase tracking-[0.35em] text-gold mb-6">With Our Thanks</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ivory font-light tracking-tight">
            Blue Light, Veterans &amp; Marshall's Discounts
          </h2>
          <p className="mt-5 text-stone-mist font-light max-w-2xl mx-auto">
            We're proud to offer a special discount to those who serve and support our community.
          </p>
        </FadeUp>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {Object.entries(SCHEMES).map(([key, meta], i) => {
            const scheme = config?.schemes?.[key];
            const enabled = scheme?.enabled !== false;
            const percent = scheme?.percent ?? 10;
            return (
              <FadeUp key={key} delay={i * 0.1}>
                <div className="border border-white/5 hover:border-gold/40 transition-colors duration-500 bg-ink-2/40 p-10 h-full flex flex-col items-center" data-testid={`scheme-${key}`}>
                  <meta.icon className="w-8 h-8 text-gold mb-5" />
                  <h3 className="font-serif text-2xl text-ivory font-light">{meta.title}</h3>
                  <p className="font-mono text-gold text-lg mt-2">{percent}% discount</p>
                  <p className="text-stone-mist text-sm font-light mt-3 flex-1">{meta.blurb}</p>
                  {enabled ? (
                    <button
                      onClick={() => setOpen(key)}
                      data-testid={`verify-${key}`}
                      className="mt-7 border border-gold/40 text-gold px-7 py-3.5 text-xs uppercase tracking-[0.25em] hover:bg-gold hover:text-ink transition-colors duration-300"
                    >
                      Verify {key === "blue_light" ? "Blue Light" : key === "veterans" ? "Veteran" : "Marshall's"} Eligibility
                    </button>
                  ) : (
                    <p className="mt-7 text-stone-mist text-xs uppercase tracking-[0.2em]">Currently unavailable</p>
                  )}
                </div>
              </FadeUp>
            );
          })}
        </div>
      </div>
      <AnimatePresence>
        {open && <VerifyModal scheme={open} config={config} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </section>
  );
}
